import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Input,
  makeStyles,
} from '@fluentui/react-components';
import { SearchRegular } from '@fluentui/react-icons';
import { useOpsGroups, groupDisplayName, groupLifecycle } from '@entities/ops-group';
import { useAuth } from '@shared/providers/AuthProvider';
import { COLORS } from '@shared/ui';

const useStyles = makeStyles({
  // ~50% wider than the original 280px (7/7/26 item 15c) — this search is the
  // fastest route to a group; make it hard to miss.
  container: {
    position: 'relative',
    width: '420px',
  },
  input: {
    width: '100%',
    backgroundColor: 'rgba(255,255,255,0.15)',
    border: '1px solid rgba(255,255,255,0.25)',
    color: '#fff',
    // Fluent's Input renders its own inner input — force the placeholder/text
    // colors so they're legible against the orange header.
    '& input': {
      color: '#fff',
    },
    '& input::placeholder': {
      color: 'rgba(255,255,255,0.7)',
    },
    '& svg': {
      color: 'rgba(255,255,255,0.85)',
    },
  },
  dropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: '4px',
    zIndex: 1000,
    maxHeight: '360px',
    overflow: 'auto',
    backgroundColor: COLORS.white,
    border: `1px solid ${COLORS.border}`,
    borderRadius: '6px',
    boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
  },
  option: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '8px 12px',
    cursor: 'pointer',
    fontSize: '14px',
    color: COLORS.textPrimary,
    ':hover': { backgroundColor: COLORS.bgSurface },
  },
  optionActive: {
    backgroundColor: COLORS.bgSurface,
  },
  optionName: { fontWeight: 500 },
  optionMeta: {
    fontSize: '12px',
    color: COLORS.textSecondary,
  },
  empty: {
    padding: '12px',
    fontSize: '13px',
    color: COLORS.textMuted,
    textAlign: 'center',
  },
  easterEggGreeting: {
    textAlign: 'center',
    fontSize: '20px',
  },
  easterEggImg: {
    display: 'block',
    width: '100%',
    maxWidth: '420px',
    margin: '8px auto 0',
    borderRadius: '6px',
  },
  hint: {
    padding: '8px 12px',
    fontSize: '12px',
    color: COLORS.textMuted,
    borderBottom: `1px solid ${COLORS.borderSubtle}`,
    backgroundColor: COLORS.bgBase,
  },
});

const MAX_RESULTS = 20;
const EASTER_EGG_TRIGGERS = ['hello vera', 'hi vera'];

export function HeaderGroupSearch() {
  const styles = useStyles();
  const navigate = useNavigate();
  // The whole list, then narrowed here rather than in OData. Two reasons: it
  // shares GroupsPage's cache entry instead of making a second round trip, and
  // it means a PRE-ENROLLED group (vba_isactive false) can be found while
  // somebody is still working its admin fee breakout - which an
  // `activeOnly` filter hid, and no OData clause on vba_provisional can do
  // safely before the column is provisioned on every org.
  const { data: allOpsGroups } = useOpsGroups();
  const opsGroups = useMemo(
    () => allOpsGroups?.filter((g) => g.isActive || g.provisional),
    [allOpsGroups],
  );
  const { displayName } = useAuth();

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [easterEggOpen, setEasterEggOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const firstName = (displayName ?? '').trim().split(/\s+/)[0] || 'there';

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !opsGroups) return [];
    const matches = opsGroups.filter((g) => {
      if (g.name?.toLowerCase().includes(q)) return true;
      if (g.accountName?.toLowerCase().includes(q)) return true;
      if (g.otherGroupNames?.toLowerCase().includes(q)) return true;
      if (g.accountNumber?.toLowerCase().includes(q)) return true;
      return false;
    });
    // Termed groups stay FINDABLE here (TERMED-GROUPS.md rule 4: never out of
    // search) but sink below the on-book matches and carry their badge text in
    // the meta line, so landing on one is a choice, not an accident.
    matches.sort(
      (a, b) => Number(groupLifecycle(a).offBook) - Number(groupLifecycle(b).offBook),
    );
    return matches.slice(0, MAX_RESULTS);
  }, [query, opsGroups]);

  const selectGroup = useCallback(
    (id: string) => {
      navigate(`/ops/groups/${id}`);
      setQuery('');
      setOpen(false);
      inputRef.current?.blur();
    },
    [navigate],
  );

  // Close dropdown on outside click
  useEffect(() => {
    function handle(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // "/" global shortcut to focus — ignored when user is already in a text input
  useEffect(() => {
    function handle(e: KeyboardEvent) {
      if (e.key !== '/') return;
      const t = e.target as HTMLElement | null;
      if (t) {
        const tag = t.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
        if (t.isContentEditable) return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      e.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
      setOpen(true);
    }
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, []);

  // Keep activeIdx in bounds as results change
  useEffect(() => {
    setActiveIdx((idx) => (results.length === 0 ? 0 : Math.min(idx, results.length - 1)));
  }, [results.length]);

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      const pick = results[activeIdx];
      if (pick) {
        e.preventDefault();
        selectGroup(pick.id);
      }
    }
  };

  return (
    <div ref={containerRef} className={styles.container}>
      <Input
        ref={inputRef}
        className={styles.input}
        contentBefore={<SearchRegular />}
        placeholder="Search groups ( / )"
        value={query}
        onChange={(_, d) => {
          const next = d.value;
          if (EASTER_EGG_TRIGGERS.includes(next.trim().toLowerCase())) {
            setEasterEggOpen(true);
            setQuery('');
            setOpen(false);
            setActiveIdx(0);
            inputRef.current?.blur();
            return;
          }
          setQuery(next);
          setOpen(next.trim().length > 0);
          setActiveIdx(0);
        }}
        onFocus={() => {
          if (query.trim().length > 0) setOpen(true);
        }}
        onKeyDown={onKeyDown}
      />
      <Dialog
        open={easterEggOpen}
        onOpenChange={(_, d) => setEasterEggOpen(d.open)}
      >
        <DialogSurface>
          <DialogBody>
            <DialogTitle className={styles.easterEggGreeting}>
              Hi, {firstName}!
            </DialogTitle>
            <DialogContent>
              <img
                src="/80s-aerobics-aerobic-championship.gif"
                alt="80s aerobics championship"
                className={styles.easterEggImg}
              />
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
      {open && query.trim().length > 0 && (
        <div className={styles.dropdown}>
          {results.length === 0 ? (
            <div className={styles.empty}>No enrolled groups match</div>
          ) : (
            <>
              {results.map((g, idx) => (
                <div
                  key={g.id}
                  className={
                    idx === activeIdx
                      ? `${styles.option} ${styles.optionActive}`
                      : styles.option
                  }
                  onMouseEnter={() => setActiveIdx(idx)}
                  onMouseDown={(e) => {
                    // mousedown (not click) so selection beats the outside-click handler
                    e.preventDefault();
                    selectGroup(g.id);
                  }}
                >
                  <span className={styles.optionName}>{groupDisplayName(g)}</span>
                  <span className={styles.optionMeta}>
                    {[
                      // "Termed 12/31/25" leads the meta so a termed pick is
                      // deliberate. "Pre-enrolled" earns the same slot: the
                      // group is off the book and half its tabs are empty.
                      groupLifecycle(g).status === 'termed' || groupLifecycle(g).provisional
                        ? groupLifecycle(g).label
                        : null,
                      // Legal name shown when it differs, so a legal-name match
                      // is recognizable under the Dynamics display name.
                      g.name !== groupDisplayName(g) ? g.name : null,
                      g.accountNumber,
                      [g.city, g.state].filter(Boolean).join(', '),
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
